import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    Grid,
    useMediaQuery,
    useTheme,
    CircularProgress,
    IconButton,
} from "@mui/material";
import logo from "../../../assets/MainLayout/sahyadriLogo.png";
import { useNavigate } from "react-router-dom";
import { postLoginEmail, verifyOtp } from "../../../utils/apiService";
import { useSnackbar } from "../../../components/KenSnackbar";
import { useAuth } from "../../../contexts/AuthContext";
import EditIcon from "@mui/icons-material/Edit";

const Login: React.FC = () => {
    const [step, setStep] = useState<"email" | "verify" | "invalid" | "success">("email");
    const [email, setEmail] = useState("");
    const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
    const [timeLeft, setTimeLeft] = useState(120);
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { login } = useAuth();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        if (step === "verify" || step === "invalid") {
            const timer =
                timeLeft > 0 && setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
            return () => clearInterval(timer as NodeJS.Timeout);
        }
    }, [step, timeLeft]);

    const handleEmailSubmit = () => {
        setLoading(true);
        if (!email.includes("@")) {
            setLoading(false);
            showSnackbar(
                "The email address you entered is not valid. Please check and try again.",
                "warning"
            );
            return;
        }

        postLoginEmail(email)
            .then((res) => {
                setLoading(false);
                if (res.success) {
                    showSnackbar("OTP has been sent to your login email.", "success");
                    alert(res.OTP);
                    setStep("verify");
                    setTimeLeft(120);
                } else {
                    showSnackbar(
                        "The email address you entered is not valid. Please check and try again.",
                        "warning"
                    );
                }
            })
            .catch((err) => {
                setLoading(false);
                showSnackbar(
                    "The email address you entered is not valid. Please check and try again.",
                    "error"
                );

                console.log(err);
            });
    };

    const handleCodeChange = (value: string, index: number) => {
        if (/^\d?$/.test(value)) {
            const newCode = [...code];
            newCode[index] = value;
            setCode(newCode);
            if (value && index < 5) {
                const next = document.getElementById(`code-${index + 1}`);
                next?.focus();
            }
        }
    };

    const handleVerify = () => {
        if (timeLeft <= 0) {
            showSnackbar("OTP expired. Please try again.", "error");
            setStep("invalid");
            setCode(["", "", "", "", "", ""]);
            return;
        }
        setLoading(true);
        const enteredCode: string = code.join("");

        console.log(email, enteredCode, "dndkjdkj");

        verifyOtp(email, enteredCode).then((res) => {
            console.log(res, "ldskndsf");
            if (res?.success) {
                setLoading(false);
                showSnackbar("You have successfully logged in.", "success");
                localStorage.setItem("contactId", res?.ContactId);
                localStorage.setItem("role", res?.ContactRecordType);
                localStorage.setItem("usn_number", res?.ContactUSNnumber);
                localStorage.setItem("contact_name", res?.ContactName);
                login(res?.ContactRecordType as "Faculty" | "Student");
                setStep("success");
                if (res?.ContactRecordType === "Faculty") {
                    navigate('/faculty/facultyExams');
                } else {
                    setLoading(false);
                    navigate('/student/myexam');
                }
            } else {
                setLoading(false);
                setStep("invalid");
                setCode(["", "", "", "", "", ""]);
                showSnackbar("The OTP you entered is incorrect. Please try again.", "error");
            }

        }).catch((err) => {
            setLoading(false);
            console.log(err);
            setStep("invalid");
            setCode(["", "", "", "", "", ""]);
        })

    };

    const handleResend = () => {
        setCode(["", "", "", "", "", ""]);
        setTimeLeft(120);
        setStep("verify");
        handleEmailSubmit();
    };

    const formattedTime = `${Math.floor(timeLeft / 60)}:${(timeLeft % 60)
        .toString()
        .padStart(2, "0")}`;

    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "#F7F9FC",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                px: { xs: 2, sm: 4, md: 6 },
                py: { xs: 4, sm: 6 },
                textAlign: "center",
            }}
        >
            {loading && (
                <Box
                    sx={{
                        position: "fixed",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255,255,255,0.7)",
                        zIndex: 9999,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
            {/* === Background Circles + Dots === */}
            <Box
                sx={{
                    position: "absolute",
                    top: "20px",
                    left: { xs: "-180px", sm: "-250px" },
                    width: { xs: "450px", sm: "680px" },
                    height: { xs: "450px", sm: "680px" },
                    border: "3px solid #FBF0CC",
                    borderRadius: "50%",
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    top: { xs: "180px", sm: "240px" },
                    left: { xs: "-60px", sm: "-90px" },
                    width: { xs: "300px", sm: "450px" },
                    height: { xs: "300px", sm: "450px" },
                    border: "3px solid #FBF0CC",
                    borderRadius: "50%",
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    top: { xs: "40px", sm: "50px" },
                    left: { xs: "160px", sm: "250px" },
                    width: { xs: "35px", sm: "50px" },
                    height: { xs: "35px", sm: "50px" },
                    backgroundColor: "#FBF0CC",
                    borderRadius: "50%",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    top: { xs: "400px", sm: "450px" },
                    left: { xs: "280px", sm: "400px" },
                    width: { xs: "18px", sm: "25px" },
                    height: { xs: "18px", sm: "25px" },
                    backgroundColor: "#FBF0CC",
                    borderRadius: "50%",
                }}
            />

            {/* === Logo === */}
            <Box sx={{ textAlign: "center", mb: 3, zIndex: 1 }}>
                <img
                    src={logo}
                    alt="Logo"
                    style={{
                        width: isMobile ? 70 : 90,
                        height: isMobile ? 70 : 90,
                        backgroundColor: "transparent",
                        mixBlendMode: "multiply",
                    }}
                />
            </Box>

            {/* === Card Section === */}
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                sx={{ zIndex: 1, width: "100%" }}
            >
                <Grid size={{ xs: 12, sm: 8, md: 4 }}>
                    <Card
                        sx={{
                            borderRadius: 2,
                            textAlign: "center",
                            backgroundColor: "#fff",
                            mx: "auto",
                            width: "100%",
                            maxWidth: 420,
                        }}
                    >
                        <CardContent>
                            {step === "email" && (
                                <>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        Student and Faculty Portal
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 3 }}
                                    >
                                        Enter your university email to receive a verification code
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="eg: student@sahyadri.edu"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleEmailSubmit();
                                            }
                                        }}
                                        sx={{
                                            mb: 3,
                                            backgroundColor: "#fff",
                                        }}
                                    />
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        sx={{
                                            backgroundColor: "#0A001A",
                                            textTransform: "none",
                                            color: "#fff",
                                            fontWeight: 500,
                                            "&:hover": { backgroundColor: "#000" },
                                            height: 36,
                                            borderRadius: 2,
                                            width: "320px",
                                        }}
                                        onClick={handleEmailSubmit}
                                    >
                                        Send Verification Code
                                    </Button>
                                </>
                            )}

                            {(step === "verify" || step === "invalid" || step === "success") && (
                                <>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        Verify your email
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        We've sent a 6-digit code to <br />
                                        <b>{email}</b>{" "}
                                        <IconButton
                                            size="small"
                                            onClick={() => setStep("email")}
                                            sx={{ color: "#0A001A" }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Typography>

                                    <Box
                                        sx={{
                                            mt: 3,
                                            mb: 2,
                                            display: "flex",
                                            gap: { xs: 0.5, sm: 1 },
                                            justifyContent: "center",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        {code.map((digit, index) => {
                                            const isFocused = focusedIndex === index;
                                            const isFilled = digit !== "";

                                            return (
                                                <TextField
                                                    key={index}
                                                    id={`code-${index}`}
                                                    value={digit}
                                                    onChange={(e) => handleCodeChange(e.target.value, index)}
                                                    onFocus={() => setFocusedIndex(index)}
                                                    onBlur={() => setFocusedIndex(null)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleVerify();
                                                        }
                                                    }}
                                                    variant="outlined"
                                                    inputProps={{
                                                        maxLength: 1,
                                                        style: {
                                                            textAlign: "center",
                                                            width: "40px",
                                                            // height: "37px",
                                                        },
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "8px",
                                                            backgroundColor: "#F6F8FB",
                                                            transition: "all 0.25s ease",
                                                            "& fieldset": {
                                                                border: step === "invalid"
                                                                    ? "0.5px solid transparent !important"
                                                                    : !isFilled
                                                                        ? "0.5px solid #CECECE !important"
                                                                        : "transparent !important",
                                                            },
                                                            "&.Mui-focused fieldset": {
                                                                borderColor:
                                                                    isFocused
                                                                        ? "#0A001A !important"
                                                                        : "transparent !important",
                                                            },
                                                        },
                                                        "& .MuiOutlinedInput-input": {
                                                            textAlign: "center",
                                                            color: "#0A001A",
                                                            fontWeight: 500,
                                                            padding: 0,
                                                        },
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{ mb: 1 }}
                                    >
                                        ⏱ Expires in {formattedTime}
                                    </Typography>

                                    {step === "invalid" && (
                                        <Typography
                                            variant="body2"
                                            color="error"
                                            sx={{
                                                mb: 1,
                                                fontWeight: 500,
                                            }}
                                        >
                                            Invalid verification code. Please try again.
                                        </Typography>
                                    )}

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        sx={{
                                            "&:hover": { backgroundColor: "#000" },
                                            width: "320px",
                                            height: "36px",
                                            borderRadius: 2,
                                            mt: 1,
                                            backgroundColor: "#0A001A",
                                            textTransform: "none",
                                            color: "#fff",
                                            fontWeight: 500,

                                        }}
                                        onClick={handleVerify}
                                    >
                                        Verify and Continue
                                    </Button>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 2 }}
                                    >
                                        Didn’t receive the code?{" "}
                                        <span
                                            onClick={handleResend}
                                            style={{
                                                color: "#000",
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Resend
                                        </span>
                                    </Typography>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{
                            position: "absolute",
                            bottom: { xs: 8, sm: 16 },
                            left: 0,
                            width: "100%",
                            textAlign: "center",
                            fontSize: { xs: "10px", sm: "12px" },
                        }}
                    >
                        © 2025 Ken42
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Login;
